import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { getItemsStatistics } from '../api/statistics';
import { getErrorMessage } from '../api/errorMessage';
import { tr } from '../i18n';
import { StatisticsItem, StatisticsResponse, StatisticsSection } from '../types';
import { EmptyState, ErrorState } from '../ui/system-states';
import { TableSkeleton } from '../ui/TableSkeleton';
import { useAppToast } from '../ui/toast';
import { useCatalog } from '../catalog';
import StatisticsEntityIcon from '../features/statistics/StatisticsEntityIcon';
import {
  formatInt,
  formatMono,
  formatPct,
  resolveEntityDisplayName,
  StatisticsEntityKind,
  statisticsDetailPath
} from '../features/statistics/statisticsEntity';
import './StatisticsPage.css';

const emptyStatistics: StatisticsResponse = {
  matchesWithState: 0,
  playerPicksWithState: 0,
  matchTypes: [],
  heroes: { overall: [], byMatchType: {} },
  abilities: { overall: [], byMatchType: {} },
  bannedHeroes: { overall: [], byMatchType: {} },
  bannedAbilities: { overall: [], byMatchType: {} }
};

const getSectionItems = (section: StatisticsSection, matchTypeCode: string): StatisticsItem[] => {
  if (!matchTypeCode) return section.overall || [];
  return section.byMatchType?.[matchTypeCode] || [];
};

const isStatisticsResponse = (v: any): v is StatisticsResponse =>
  Boolean(v && typeof v === 'object' && !Array.isArray(v) && v.heroes && v.abilities);

// ─── types ────────────────────────────────────────────────────────────────────

type ToggleMode = 'pick' | 'ban';

type TopTableProps = {
  title: string;
  pickItems: StatisticsItem[];
  banItems?: StatisticsItem[];
  sub?: string;
  loading: boolean;
  itemKind: StatisticsEntityKind;
};

// ─── TopTable ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

type SortKey = 'rate' | 'place';
type SortDir = 'asc' | 'desc';

const TopTable: React.FC<TopTableProps> = ({
  title,
  pickItems,
  banItems,
  sub: subOverride,
  loading,
  itemKind
}) => {
  const catalog = useCatalog();
  const [toggle, setToggle] = useState<ToggleMode>('pick');
  const [sortKey, setSortKey] = useState<SortKey>('rate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const source = toggle === 'ban' && banItems ? banItems : pickItems;
    const arr = [...source];
    arr.sort((a, b) => {
      if (sortKey === 'place') {
        // avgPlace может быть null — отправляем такие в конец независимо от направления
        const av = a.avgPlace ?? Number.POSITIVE_INFINITY;
        const bv = b.avgPlace ?? Number.POSITIVE_INFINITY;
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc' ? a.pickRate - b.pickRate : b.pickRate - a.pickRate;
    });
    return arr;
  }, [toggle, pickItems, banItems, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const rows = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const sub = subOverride ?? (toggle === 'ban' ? tr.players.statistics.byBanRate : tr.players.statistics.byPickRate);

  // Шкала бара — по максимуму всего набора, чтобы не «прыгала» между страницами.
  const maxRate = useMemo(
    () => Math.max(0.001, ...sorted.map(r => r.pickRate)),
    [sorted]
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'place' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const changeToggle = (next: ToggleMode) => {
    setToggle(next);
    setPage(0);
  };

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  const rateLabel = toggle === 'ban' ? 'Ban Rate' : 'Pick Rate';
  const countLabel = toggle === 'ban' ? tr.players.statistics.columns.bans : tr.players.statistics.columns.picks;

  return (
    <div className="stp-panel">
      <div className="stp-panel-header">
        <span className="stp-panel-title">{title}</span>
        <span className="stp-panel-sub">{sub}</span>
        <div className="stp-panel-gap" />
        {banItems && (
          <div className="stp-toggle-group">
            <button
              type="button"
              className={`stp-toggle-btn${toggle === 'pick' ? ' active' : ''}`}
              onClick={() => changeToggle('pick')}
            >
              Pick
            </button>
            <button
              type="button"
              className={`stp-toggle-btn${toggle === 'ban' ? ' active' : ''}`}
              onClick={() => changeToggle('ban')}
            >
              Ban
            </button>
          </div>
        )}
      </div>

      <table className="stp-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{tr.players.statistics.columns.name}</th>
            <th
              className={`stp-th-sortable${sortKey === 'rate' ? ' active' : ''}`}
              onClick={() => handleSort('rate')}
            >
              {rateLabel}{sortArrow('rate')}
            </th>
            <th>{tr.players.statistics.columns.matches}</th>
            <th>{countLabel}</th>
            <th
              className={`stp-th-sortable${sortKey === 'place' ? ' active' : ''}`}
              onClick={() => handleSort('place')}
            >
              {tr.players.statistics.columns.avgPlace}{sortArrow('place')}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? (
            <tr className="stp-state-row">
              <td colSpan={6} style={{ padding: 0 }}>
                <TableSkeleton
                  rows={8}
                  columns={['6%', '34%', '15%', '15%', '15%', '15%']}
                  showHeader={false}
                  ariaLabel={tr.players.statistics.loadingAria}
                />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr className="stp-state-row">
              <td colSpan={6}>
                <EmptyState title={tr.common.states.noData} description={tr.players.statistics.emptyDescription} />
              </td>
            </tr>
          ) : (
            rows.map((item, idx) => {
              const barPct = Math.min(100, Math.round((item.pickRate / maxRate) * 100));
              const isBanToggle = toggle === 'ban';
              return (
                <tr key={item.name}>
                  <td data-label="#">
                    <span className="stp-cell-rank">{safePage * PAGE_SIZE + idx + 1}</span>
                  </td>
                  <td data-label={tr.players.statistics.columns.name} className="stp-td-name">
                    <div className="stp-cell-name-wrap">
                      <Link className="stp-cell-link" to={statisticsDetailPath(itemKind, item.name)}>
                        <StatisticsEntityIcon name={item.name} kind={itemKind} />
                        <span className="stp-cell-name">{resolveEntityDisplayName(catalog, itemKind, item.name)}</span>
                      </Link>
                    </div>
                  </td>
                  <td data-label={rateLabel}>
                    <div className="stp-cell-rate-wrap">
                      <span className="stp-cell-rate-val">{formatPct(item.pickRate)}</span>
                      <div className="stp-rate-bar">
                        <div
                          className={`stp-rate-bar-fill${isBanToggle ? ' ban' : ''}`}
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td data-label={tr.players.statistics.columns.matches}>
                    <span className="stp-cell-mono">{formatInt(item.matchesCount)}</span>
                  </td>
                  <td data-label={countLabel}>
                    <span className="stp-cell-mono">{formatInt(item.picksCount)}</span>
                  </td>
                  <td data-label={tr.players.statistics.columns.avgPlace}>
                    <span className="stp-cell-mono">{formatMono(item.avgPlace)}</span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {!loading && sorted.length > PAGE_SIZE && (
        <div className="stp-pagination">
          <button
            type="button"
            className="stp-page-btn"
            disabled={safePage === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
          >
            {tr.players.pagination.back}
          </button>
          <span className="stp-page-info">
            {tr.players.pagination.info(safePage + 1, totalPages, sorted.length)}
          </span>
          <button
            type="button"
            className="stp-page-btn"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
          >
            {tr.players.pagination.next}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── mode tabs config ─────────────────────────────────────────────────────────

type ModeTab = { label: string; code: string };

const MODE_TABS: ModeTab[] = [
  { label: tr.players.statistics.modeAll, code: '' },
  { label: 'Solo', code: 'PVP_SOLO' },
  { label: 'Duo', code: 'PVP_DUO' }
];

// ─── main ────────────────────────────────────────────────────────────────────

const StatisticsPage: React.FC = () => {
  const [statistics, setStatistics] = useState<StatisticsResponse>(emptyStatistics);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchTypeCode, setMatchTypeCode] = useState('');
  const [items, setItems] = useState<StatisticsItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const { pushToast } = useAppToast();

  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<StatisticsResponse>('/api/statistics');
      setStatistics(isStatisticsResponse(res.data) ? res.data : emptyStatistics);
    } catch (err: any) {
      const msg = getErrorMessage(err, tr.players.statistics.loadFailed);
      setError(msg);
      pushToast(msg);
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  const loadItems = useCallback(async () => {
    try {
      setItemsLoading(true);
      setItemsError(null);
      const response = await getItemsStatistics();
      setItems(response?.items ?? []);
    } catch (err: any) {
      setItemsError(getErrorMessage(err, tr.players.statistics.itemsLoadFailed));
    } finally {
      setItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Derived sections
  const sections = useMemo(
    () => ({
      heroes: getSectionItems(statistics.heroes, matchTypeCode),
      abilities: getSectionItems(statistics.abilities, matchTypeCode),
      bannedHeroes: getSectionItems(statistics.bannedHeroes, matchTypeCode),
      bannedAbilities: getSectionItems(statistics.bannedAbilities, matchTypeCode)
    }),
    [matchTypeCode, statistics]
  );

  // Total matches for current mode
  const totalMatches =
    matchTypeCode
      ? (statistics.matchTypes || []).find(t => t.matchTypeCode === matchTypeCode)?.matchesWithState ?? 0
      : statistics.matchesWithState;

  return (
    <div className="stp-page">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="stp-header">
        <h1 className="stp-title">{tr.players.statistics.title}</h1>
      </div>

      {/* ── Controls ────────────────────────────────────────── */}
      <div className="stp-controls">
        {/* Mode tabs */}
        <div className="stp-tabs">
          {MODE_TABS.map(tab => (
            <button
              key={tab.code}
              type="button"
              className={`stp-tab${matchTypeCode === tab.code ? ' active' : ''}`}
              onClick={() => setMatchTypeCode(tab.code)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="stp-controls-gap" />

        {/* Player dropdown — TODO Phase 2 */}
        <select className="stp-select" disabled title={tr.players.statistics.playerFilterTitle}>
          <option>{tr.players.statistics.playerFilterAll}</option>
        </select>
      </div>

      {/* ── Error ───────────────────────────────────────────── */}
      {error && !loading && (
        <ErrorState
          title={tr.players.statistics.errorTitle}
          description={error}
          actions={[{ label: tr.common.actions.retry, onClick: loadStatistics }]}
        />
      )}

      {/* ── KPI row ─────────────────────────────────────────── */}
      {!error && (
        <div className="stp-kpi-row">
          <div className="stp-kpi-tile">
            <span className="stp-kpi-label">{tr.players.statistics.totalMatches}</span>
            <span className="stp-kpi-value">
              {loading ? '—' : formatInt(totalMatches)}
            </span>
            <span className="stp-kpi-sub">{tr.players.statistics.perSeason}</span>
          </div>
        </div>
      )}

      {/* ── Two-column: heroes + abilities ──────────────────── */}
      {!error && (
        <div className="stp-two-col">
          <TopTable
            title={tr.players.statistics.topHeroes}
            pickItems={sections.heroes}
            banItems={sections.bannedHeroes}
            loading={loading}
            itemKind="hero"
          />
          <TopTable
            title={tr.players.statistics.topAbilities}
            pickItems={sections.abilities}
            banItems={sections.bannedAbilities}
            loading={loading}
            itemKind="ability"
          />
        </div>
      )}

      {/* ── Items (all modes, no bans) ──────────────────────── */}
      {itemsError && !itemsLoading ? (
        <ErrorState
          title={tr.players.statistics.errorTitle}
          description={itemsError}
          actions={[{ label: tr.common.actions.retry, onClick: loadItems }]}
        />
      ) : (
        <TopTable
          title={tr.players.statistics.topItems}
          sub={tr.players.statistics.itemsSub}
          pickItems={items}
          loading={itemsLoading}
          itemKind="item"
        />
      )}
    </div>
  );
};

export default StatisticsPage;
